import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Shield, 
  Users, 
  Film, 
  CreditCard, 
  LogOut, 
  Search,
  ChevronRight,
  TrendingUp,
  UserPlus,
  Crown,
  Star,
  Trash2,
  Edit,
  Eye,
  RefreshCw,
  X,
  Trophy,
  CheckCircle,
  Clock,
  Award,
  MessageSquare,
  Mail,
  Download,
  ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [subscriptions, setSubscriptions] = useState([]);
  const [challengeEntries, setChallengeEntries] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingUsers, setExportingUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/admin/stats`, {
        headers: getAuthHeaders()
      });
      if (statsRes.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
        return;
      }
      const statsData = await statsRes.json();
      setStats(statsData);

      // Fetch users
      await fetchUsers();

      // Fetch subscriptions
      const subsRes = await fetch(`${API_URL}/api/admin/subscriptions?limit=20`, {
        headers: getAuthHeaders()
      });
      const subsData = await subsRes.json();
      setSubscriptions(subsData.events || []);

      // Fetch challenge entries
      const challengeRes = await fetch(`${API_URL}/api/admin/challenge-entries`, {
        headers: getAuthHeaders()
      });
      if (challengeRes.ok) {
        const challengeData = await challengeRes.json();
        setChallengeEntries(challengeData || []);
      }

      // Fetch contact messages
      const messagesRes = await fetch(`${API_URL}/api/admin/contact-messages`, {
        headers: getAuthHeaders()
      });
      if (messagesRes.ok) {
        const messagesData = await messagesRes.json();
        setContactMessages(messagesData.messages || []);
      }

    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (search = '', tier = 'all') => {
    try {
      let url = `${API_URL}/api/admin/users?limit=50`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (tier && tier !== 'all') url += `&tier=${tier}`;

      const res = await fetch(url, { headers: getAuthHeaders() });
      const data = await res.json();
      setUsers(data.users || []);
      setTotalUsers(data.total || 0);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const handleSearch = () => {
    fetchUsers(searchQuery, tierFilter);
  };

  const fetchUserDetail = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setUserDetail(data);
      setSelectedUser(userId);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const updateUserTier = async (userId, newTier) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ subscription_tier: newTier })
      });
      if (res.ok) {
        toast.success('User subscription updated');
        fetchUsers(searchQuery, tierFilter);
        if (userDetail) fetchUserDetail(userId);
      }
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const deleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user and all their data? This cannot be undone.')) {
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('User deleted');
        setSelectedUser(null);
        setUserDetail(null);
        fetchUsers(searchQuery, tierFilter);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const updateChallengeEntryStatus = async (entryId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/challenge-entries/${entryId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        toast.success('Entry status updated');
        // Refresh challenge entries
        const challengeRes = await fetch(`${API_URL}/api/admin/challenge-entries`, {
          headers: getAuthHeaders()
        });
        if (challengeRes.ok) {
          const challengeData = await challengeRes.json();
          setChallengeEntries(challengeData || []);
        }
      }
    } catch (error) {
      toast.error('Failed to update entry status');
    }
  };

  const exportUsers = async (format = 'csv') => {
    setExportingUsers(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/export?format=${format}`, {
        headers: getAuthHeaders()
      });
      
      if (format === 'json') {
        const data = await res.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `moviebot_users_${new Date().toISOString().slice(0,10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `moviebot_users_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
      
      toast.success(`Users exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export users');
    } finally {
      setExportingUsers(false);
    }
  };

  const updateContactMessageStatus = async (contactId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/contact-messages/${contactId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        toast.success('Message status updated');
        // Refresh messages
        const messagesRes = await fetch(`${API_URL}/api/admin/contact-messages`, {
          headers: getAuthHeaders()
        });
        if (messagesRes.ok) {
          const messagesData = await messagesRes.json();
          setContactMessages(messagesData.messages || []);
        }
      }
    } catch (error) {
      toast.error('Failed to update message status');
    }
  };

  const deleteContactMessage = async (contactId) => {
    if (!confirm('Are you sure you want to delete this message?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/contact-messages/${contactId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Message deleted');
        setContactMessages(prev => prev.filter(m => m.contact_id !== contactId));
      }
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const getMessageStatusBadge = (status) => {
    switch (status) {
      case 'read':
        return <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold uppercase">Read</span>;
      case 'replied':
        return <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-bold uppercase">Replied</span>;
      case 'resolved':
        return <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs font-bold uppercase">Resolved</span>;
      default:
        return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold uppercase">New</span>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'winner':
        return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold uppercase flex items-center gap-1"><Trophy className="w-3 h-3" />Winner</span>;
      case 'finalist':
        return <span className="px-2 py-1 bg-teal-500/20 text-teal-400 text-xs font-bold uppercase flex items-center gap-1"><Award className="w-3 h-3" />Finalist</span>;
      case 'reviewing':
        return <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold uppercase flex items-center gap-1"><Eye className="w-3 h-3" />Reviewing</span>;
      case 'rejected':
        return <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-bold uppercase">Rejected</span>;
      default:
        return <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs font-bold uppercase flex items-center gap-1"><Clock className="w-3 h-3" />Pending</span>;
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin');
  };

  const getTierBadge = (tier) => {
    switch (tier) {
      case 'pro':
        return <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs font-bold uppercase">Pro</span>;
      case 'basic':
        return <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold uppercase">Basic</span>;
      default:
        return <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs font-bold uppercase">Free</span>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-red-500" />
            <span className="font-bold text-white">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-muted-foreground text-sm">
              {localStorage.getItem('admin_username')}
            </span>
            <Button
              onClick={logout}
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="pt-20 px-6 pb-12 max-w-7xl mx-auto">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {['overview', 'users', 'subscriptions', 'challenge', 'messages'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === tab
                  ? tab === 'challenge' ? 'bg-teal-500 text-white' 
                    : tab === 'messages' ? 'bg-purple-500 text-white'
                    : 'bg-red-500 text-white'
                  : 'bg-white/5 text-muted-foreground hover:text-white'
              }`}
            >
              {tab === 'challenge' && <Trophy className="w-4 h-4" />}
              {tab === 'messages' && <MessageSquare className="w-4 h-4" />}
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === 'challenge' && challengeEntries.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">{challengeEntries.length}</span>
              )}
              {tab === 'messages' && contactMessages.filter(m => m.status === 'new').length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">{contactMessages.filter(m => m.status === 'new').length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-6 bg-secondary/30 border border-white/10"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500/20 flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm">Total Users</p>
                    <p className="text-2xl font-bold text-white">{stats.users.total}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="p-6 bg-secondary/30 border border-white/10"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500/20 flex items-center justify-center">
                    <UserPlus className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm">New This Week</p>
                    <p className="text-2xl font-bold text-white">{stats.users.recent_signups_7d}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="p-6 bg-secondary/30 border border-white/10"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-500/20 flex items-center justify-center">
                    <Crown className="w-6 h-6 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm">Paid Users</p>
                    <p className="text-2xl font-bold text-white">
                      {stats.users.by_tier.basic + stats.users.by_tier.pro}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="p-6 bg-secondary/30 border border-white/10"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-yellow-500/20 flex items-center justify-center">
                    <Film className="w-6 h-6 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm">Total Projects</p>
                    <p className="text-2xl font-bold text-white">{stats.projects.total}</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* User Breakdown & Projects by Genre */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="p-6 bg-secondary/30 border border-white/10">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-red-500" />
                  Users by Subscription
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-black/30">
                    <span className="text-white">Free</span>
                    <span className="text-muted-foreground">{stats.users.by_tier.free}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-black/30">
                    <span className="text-blue-400">Basic ($5.99/mo)</span>
                    <span className="text-muted-foreground">{stats.users.by_tier.basic}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-black/30">
                    <span className="text-purple-400">Pro ($19.99/mo)</span>
                    <span className="text-muted-foreground">{stats.users.by_tier.pro}</span>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-secondary/30 border border-white/10">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <Film className="w-5 h-5 text-red-500" />
                  Projects by Genre
                </h3>
                <div className="space-y-2">
                  {Object.entries(stats.projects.by_genre).slice(0, 6).map(([genre, count]) => (
                    <div key={genre} className="flex items-center justify-between p-2 bg-black/30">
                      <span className="text-white capitalize">{genre}</span>
                      <span className="text-muted-foreground">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Page Analytics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="p-6 bg-secondary/30 border border-white/10">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-red-500" />
                  Page Views (Last 7 Days)
                </h3>
                {stats.page_analytics?.page_views?.length > 0 ? (
                  <div className="space-y-2">
                    {stats.page_analytics.page_views.map((pv, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-black/30">
                        <span className="text-white text-sm">{pv.page || 'Unknown'}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-muted-foreground text-xs">{pv.unique_users} users</span>
                          <span className="text-primary font-bold">{pv.views} views</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">No page view data yet</p>
                )}
              </div>

              <div className="p-6 bg-secondary/30 border border-white/10">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-red-500" />
                  Daily Active Users (Last 7 Days)
                </h3>
                {stats.page_analytics?.daily_active_users?.length > 0 ? (
                  <div className="space-y-2">
                    {stats.page_analytics.daily_active_users.map((day, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-black/30">
                        <span className="text-white text-sm">{day.date}</span>
                        <span className="text-primary font-bold">{day.active_users} users</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">No activity data yet</p>
                )}
              </div>
            </div>

            {/* Total Scenes */}
            <div className="p-6 bg-secondary/30 border border-white/10">
              <h3 className="text-white font-bold mb-2 flex items-center gap-2">
                <Film className="w-5 h-5 text-red-500" />
                Content Created
              </h3>
              <p className="text-3xl font-bold text-white">{stats.scenes?.total || 0} <span className="text-sm font-normal text-muted-foreground">scenes generated</span></p>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Search & Filter */}
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <Input
                  placeholder="Search by email or name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="bg-black/50 border-white/10 text-white h-12"
                />
              </div>
              <Select value={tierFilter} onValueChange={(v) => { setTierFilter(v); fetchUsers(searchQuery, v); }}>
                <SelectTrigger className="w-40 bg-black/50 border-white/10 text-white h-12">
                  <SelectValue placeholder="All tiers" />
                </SelectTrigger>
                <SelectContent className="bg-secondary border-white/10">
                  <SelectItem value="all" className="text-white">All Tiers</SelectItem>
                  <SelectItem value="free" className="text-white">Free</SelectItem>
                  <SelectItem value="basic" className="text-white">Basic</SelectItem>
                  <SelectItem value="pro" className="text-white">Pro</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleSearch} className="bg-red-600 hover:bg-red-700 h-12">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
              <div className="flex gap-2">
                <Button 
                  onClick={() => exportUsers('csv')} 
                  disabled={exportingUsers}
                  variant="outline" 
                  className="border-green-500/50 text-green-400 hover:bg-green-500/10 h-12"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {exportingUsers ? 'Exporting...' : 'Export CSV'}
                </Button>
                <Button 
                  onClick={() => exportUsers('json')} 
                  disabled={exportingUsers}
                  variant="outline" 
                  className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 h-12"
                >
                  <Download className="w-4 h-4 mr-2" />
                  JSON
                </Button>
              </div>
            </div>

            <p className="text-muted-foreground text-sm">{totalUsers} users found</p>

            {/* Users Table */}
            <div className="bg-secondary/30 border border-white/10 overflow-hidden">
              <table className="w-full">
                <thead className="bg-black/30">
                  <tr>
                    <th className="text-left p-4 text-muted-foreground text-sm font-medium">User</th>
                    <th className="text-left p-4 text-muted-foreground text-sm font-medium">Tier</th>
                    <th className="text-left p-4 text-muted-foreground text-sm font-medium">Projects</th>
                    <th className="text-left p-4 text-muted-foreground text-sm font-medium">Joined</th>
                    <th className="text-right p-4 text-muted-foreground text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.user_id} className="border-t border-white/5 hover:bg-white/5">
                      <td className="p-4">
                        <div>
                          <p className="text-white font-medium">{user.name}</p>
                          <p className="text-muted-foreground text-sm">{user.email}</p>
                        </div>
                      </td>
                      <td className="p-4">{getTierBadge(user.subscription_tier)}</td>
                      <td className="p-4 text-white">{user.project_count}</td>
                      <td className="p-4 text-muted-foreground text-sm">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => fetchUserDetail(user.user_id)}
                            className="text-muted-foreground hover:text-white"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteUser(user.user_id)}
                            className="text-muted-foreground hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <div className="space-y-6">
            <h3 className="text-white font-bold flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-red-500" />
              Recent Subscription Activity
            </h3>

            <div className="bg-secondary/30 border border-white/10">
              {subscriptions.length === 0 ? (
                <p className="p-8 text-center text-muted-foreground">No subscription events yet</p>
              ) : (
                <div className="divide-y divide-white/5">
                  {subscriptions.map((event, i) => (
                    <div key={i} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="text-white">
                          {event.user_name || event.user_email || event.user_id}
                        </p>
                        <p className="text-muted-foreground text-sm">
                          {event.type === 'admin_change' ? (
                            <>Admin changed: {event.from_tier} → {event.to_tier}</>
                          ) : (
                            <>Subscribed to {event.to_tier || event.tier}</>
                          )}
                        </p>
                      </div>
                      <span className="text-muted-foreground text-sm">
                        {formatDate(event.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Challenge Tab */}
        {activeTab === 'challenge' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-bold flex items-center gap-2">
                <Trophy className="w-5 h-5 text-teal-500" />
                Summer Competition Entries
              </h3>
              <div className="text-sm text-muted-foreground">
                {challengeEntries.length} total entries
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { label: 'Total', count: challengeEntries.length, color: 'teal' },
                { label: 'Pending', count: challengeEntries.filter(e => e.status === 'pending').length, color: 'gray' },
                { label: 'Reviewing', count: challengeEntries.filter(e => e.status === 'reviewing').length, color: 'blue' },
                { label: 'Finalists', count: challengeEntries.filter(e => e.status === 'finalist').length, color: 'purple' },
                { label: 'Winners', count: challengeEntries.filter(e => e.status === 'winner').length, color: 'yellow' },
              ].map((stat) => (
                <div key={stat.label} className={`p-4 bg-${stat.color}-500/10 border border-${stat.color}-500/20`}>
                  <p className={`text-2xl font-black text-${stat.color}-400`}>{stat.count}</p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Entries Table */}
            <div className="bg-secondary/30 border border-white/10 overflow-hidden">
              {challengeEntries.length === 0 ? (
                <p className="p-8 text-center text-muted-foreground">No challenge entries yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-black/30 border-b border-white/10">
                        <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">User</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Project</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Genre</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Submitted</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {challengeEntries.map((entry) => (
                        <tr key={entry.entry_id} className="hover:bg-white/5">
                          <td className="px-4 py-4">
                            <div>
                              <p className="text-white font-medium">{entry.user_name}</p>
                              <p className="text-muted-foreground text-sm">{entry.user_email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <p className="text-white">{entry.project_title}</p>
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-muted-foreground capitalize">{entry.project_genre}</span>
                          </td>
                          <td className="px-4 py-4">
                            {getStatusBadge(entry.status)}
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-muted-foreground text-sm">{formatDate(entry.created_at)}</span>
                          </td>
                          <td className="px-4 py-4">
                            <Select
                              value={entry.status}
                              onValueChange={(value) => updateChallengeEntryStatus(entry.entry_id, value)}
                            >
                              <SelectTrigger className="w-32 h-8 bg-black/30 border-white/10 text-white text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="reviewing">Reviewing</SelectItem>
                                <SelectItem value="finalist">Finalist</SelectItem>
                                <SelectItem value="winner">Winner</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Confirmations Legend */}
            <div className="p-4 bg-black/30 border border-white/10">
              <h4 className="text-white font-medium mb-2 text-sm">Entry Confirmations Checklist</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-3 h-3 text-teal-400" />
                  <span>Story Foundation Complete</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-3 h-3 text-teal-400" />
                  <span>Following @moviebot.tech</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-3 h-3 text-teal-400" />
                  <span>Shared Challenge</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-3 h-3 text-teal-400" />
                  <span>Original Work</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-purple-500" />
                Contact Messages
              </h2>
              <span className="text-muted-foreground text-sm">
                {contactMessages.length} total • {contactMessages.filter(m => m.status === 'new').length} new
              </span>
            </div>

            {contactMessages.length === 0 ? (
              <div className="p-12 bg-secondary/30 border border-white/10 text-center">
                <MessageSquare className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">No contact messages yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {contactMessages.map((message) => (
                  <div 
                    key={message.contact_id} 
                    className={`p-6 border ${message.status === 'new' ? 'bg-purple-500/10 border-purple-500/30' : 'bg-secondary/30 border-white/10'}`}
                  >
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-white font-bold">{message.name}</h3>
                          {getMessageStatusBadge(message.status)}
                        </div>
                        <a 
                          href={`mailto:${message.email}`} 
                          className="text-primary hover:underline text-sm flex items-center gap-1"
                        >
                          <Mail className="w-3 h-3" />
                          {message.email}
                        </a>
                      </div>
                      <div className="text-right">
                        <p className="text-muted-foreground text-sm">{formatDate(message.created_at)}</p>
                      </div>
                    </div>

                    {message.subject && (
                      <p className="text-muted-foreground text-sm mb-2">
                        <strong>Subject:</strong> {message.subject}
                      </p>
                    )}

                    <div className="p-4 bg-black/30 mb-4">
                      <p className="text-white whitespace-pre-wrap">{message.message}</p>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        <Select 
                          value={message.status} 
                          onValueChange={(v) => updateContactMessageStatus(message.contact_id, v)}
                        >
                          <SelectTrigger className="w-32 bg-black/50 border-white/10 text-white h-9 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-secondary border-white/10">
                            <SelectItem value="new" className="text-white">New</SelectItem>
                            <SelectItem value="read" className="text-white">Read</SelectItem>
                            <SelectItem value="replied" className="text-white">Replied</SelectItem>
                            <SelectItem value="resolved" className="text-white">Resolved</SelectItem>
                          </SelectContent>
                        </Select>
                        <a 
                          href={`mailto:${message.email}?subject=Re: ${message.subject || 'Your MovieBot Inquiry'}`}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm transition-colors"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Reply
                        </a>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteContactMessage(message.contact_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {selectedUser && userDetail && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0a0a0a] border border-white/10 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">User Details</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSelectedUser(null); setUserDetail(null); }}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              {/* User Info */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-bold text-lg">{userDetail.user.name}</h3>
                    <p className="text-muted-foreground">{userDetail.user.email}</p>
                  </div>
                  {getTierBadge(userDetail.user.subscription_tier)}
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-black/30">
                    <p className="text-muted-foreground">User ID</p>
                    <p className="text-white font-mono">{userDetail.user.user_id}</p>
                  </div>
                  <div className="p-3 bg-black/30">
                    <p className="text-muted-foreground">Joined</p>
                    <p className="text-white">{formatDate(userDetail.user.created_at)}</p>
                  </div>
                </div>

                {/* Change Tier */}
                <div className="p-4 bg-secondary/30 border border-white/10">
                  <p className="text-white font-medium mb-3">Change Subscription Tier</p>
                  <div className="flex gap-2">
                    {['free', 'basic', 'pro'].map((tier) => (
                      <Button
                        key={tier}
                        size="sm"
                        variant={userDetail.user.subscription_tier === tier ? 'default' : 'outline'}
                        onClick={() => updateUserTier(userDetail.user.user_id, tier)}
                        className={userDetail.user.subscription_tier === tier ? 'bg-red-600' : 'border-white/20 text-white'}
                      >
                        {tier.charAt(0).toUpperCase() + tier.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Onboarding Data */}
                {userDetail.user.onboarding_completed && userDetail.user.onboarding_data && (
                  <div className="p-4 bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/30 rounded-lg">
                    <h4 className="text-primary font-bold mb-4 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Onboarding Responses
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="p-3 bg-black/30 rounded">
                        <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Country</p>
                        <p className="text-white font-medium">
                          {userDetail.user.country || userDetail.user.onboarding_data?.country || 'Not set'}
                        </p>
                      </div>
                      <div className="p-3 bg-black/30 rounded">
                        <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Storytelling Style</p>
                        <p className="text-white font-medium capitalize">
                          {userDetail.user.storytelling_style || userDetail.user.onboarding_data?.storytelling_style || 'Not set'}
                        </p>
                      </div>
                      <div className="p-3 bg-black/30 rounded">
                        <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Primary Goal</p>
                        <p className="text-white font-medium">
                          {userDetail.user.onboarding_data?.primary_goal?.replace(/_/g, ' ') || 'Not set'}
                        </p>
                      </div>
                      <div className="p-3 bg-black/30 rounded">
                        <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Experience Level</p>
                        <p className="text-white font-medium capitalize">
                          {userDetail.user.onboarding_data?.experience_level || 'Not set'}
                        </p>
                      </div>
                      <div className="p-3 bg-black/30 rounded">
                        <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Project Type</p>
                        <p className="text-white font-medium">
                          {userDetail.user.onboarding_data?.project_type?.replace(/_/g, ' ') || 'Not set'}
                        </p>
                      </div>
                      <div className="p-3 bg-black/30 rounded">
                        <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Referral Source</p>
                        <p className="text-white font-medium capitalize">
                          {userDetail.user.onboarding_data?.referral_source || 'Not set'}
                        </p>
                      </div>
                      <div className="p-3 bg-black/30 rounded col-span-2">
                        <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Referral Detail</p>
                        <p className="text-white font-medium">
                          {userDetail.user.onboarding_data?.referral_detail || 'N/A'}
                        </p>
                      </div>
                    </div>
                    {userDetail.user.onboarding_data?.completed_at && (
                      <p className="text-muted-foreground text-xs mt-3">
                        Completed: {formatDate(userDetail.user.onboarding_data.completed_at)}
                      </p>
                    )}
                  </div>
                )}
                
                {!userDetail.user.onboarding_completed && (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-amber-400 text-sm flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Onboarding not completed
                    </p>
                  </div>
                )}
              </div>

              {/* Projects & Scenes */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-bold">Projects ({userDetail.projects.length})</h4>
                  <span className="text-primary font-bold">{userDetail.total_scenes || 0} Total Scenes</span>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {userDetail.projects.map((project) => (
                    <div key={project.project_id} className="p-3 bg-black/30 border border-white/5">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-white font-medium">{project.title}</p>
                          <p className="text-muted-foreground text-xs capitalize">{project.genre}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-primary font-bold text-lg">{project.scene_count || 0}</span>
                          <p className="text-muted-foreground text-xs">scenes</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Created: {formatDate(project.created_at)}</span>
                        {project.logline && (
                          <span className="truncate max-w-[200px]" title={project.logline}>
                            {project.logline}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {userDetail.projects.length === 0 && (
                    <p className="text-muted-foreground">No projects yet</p>
                  )}
                </div>
              </div>

              {/* Danger Zone */}
              <div className="p-4 bg-red-500/10 border border-red-500/30">
                <h4 className="text-red-400 font-bold mb-3">Danger Zone</h4>
                <Button
                  onClick={() => deleteUser(userDetail.user.user_id)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete User & All Data
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
